import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry } from 'rxjs';
import { environment, AuthUserDTO } from '@sandbox-platform/shared-ui';
import {
  RegisterDTO,
  LoginDTO,
  AuthResponseDTO,
  RefreshTokenDTO,
  LogoutDTO,
  ChangePasswordDTO,
  ForgotPasswordDTO,
  ResetPasswordDTO,
  VerifyEmailDTO,
  ResendVerificationDTO,
  TokenResponseDTO,
  RegistrationWaitingListResponseDTO,
  ApiResponse,
  UpdateProfileDTO,
  UserSessionDTO,
  UserCompaniesDTO,
  LoginFlowResponseDTO,
} from '../types/auth.types';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly baseUrl = environment.authApiUrl;
  private readonly defaultTimeout = 10000; // 10 seconds
  private readonly retryAttempts = 2;

  constructor(private http: HttpClient) {}

  /**
   * Register a new user
   * @param data - RegisterDTO
   * @returns Observable<ApiResponse<RegistrationWaitingListResponseDTO>>
   */
  register(
    data: RegisterDTO
  ): Observable<ApiResponse<RegistrationWaitingListResponseDTO>> {
    return this.http
      .post<ApiResponse<RegistrationWaitingListResponseDTO>>(
        `${this.baseUrl}/auth/register`,
        data,
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Login a user
   * @param data - LoginDTO
   * @returns Observable<ApiResponse<LoginFlowResponseDTO>>
   */
  login(data: LoginDTO): Observable<ApiResponse<LoginFlowResponseDTO>> {
    return this.http
      .post<ApiResponse<LoginFlowResponseDTO>>(
        `${this.baseUrl}/auth/login`,
        data,
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }


  /**
   * Refresh token
   * @param data - RefreshTokenDTO
   * @returns Observable<ApiResponse<TokenResponseDTO>>
   */
  refreshToken(
    data: RefreshTokenDTO
  ): Observable<ApiResponse<TokenResponseDTO>> {
    return this.http
      .post<ApiResponse<TokenResponseDTO>>(
        `${this.baseUrl}/auth/refresh`,
        { refresh_token: data.refresh_token },
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Forgot password
   * @param data - ForgotPasswordDTO
   * @returns Observable<ApiResponse<null>>
   */
  forgotPassword(data: ForgotPasswordDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/forgot-password`, data, {
        headers: this.getHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Reset password
   * @param data - ResetPasswordDTO
   * @returns Observable<ApiResponse<null>>
   */
  resetPassword(data: ResetPasswordDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/reset-password`, data, {
        headers: this.getHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Verify email
   * @param data - VerifyEmailDTO
   * @returns Observable<ApiResponse<null>>
   */
  verifyEmail(data: VerifyEmailDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/verify-email`, data, {
        headers: this.getHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Resend verification
   * @param data - ResendVerificationDTO
   * @returns Observable<ApiResponse<null>>
   */
  resendVerification(
    data: ResendVerificationDTO
  ): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(
        `${this.baseUrl}/auth/resend-verification`,
        data,
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Logout
   * @param data - LogoutDTO
   * @returns Observable<ApiResponse<null>>
   */
  logout(data: LogoutDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/logout`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
  }

  /**
   * Change password
   * @param data - ChangePasswordDTO
   * @returns Observable<ApiResponse<null>>
   */
  changePassword(data: ChangePasswordDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/change-password`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get profile
   * @returns Observable<ApiResponse<AuthUserDTO>>
   */
  getProfile(): Observable<ApiResponse<AuthUserDTO>> {
    return this.http
      .get<ApiResponse<AuthUserDTO>>(`${this.baseUrl}/users/profile`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Update profile
   * @param data - UpdateProfileDTO
   * @returns Observable<ApiResponse<null>>
   */
  updateProfile(data: UpdateProfileDTO): Observable<ApiResponse<null>> {
    return this.http
      .put<ApiResponse<null>>(`${this.baseUrl}/users/profile`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get user sessions
   * @returns Observable<ApiResponse<UserSessionDTO[]>>
   */
  getUserSessions(): Observable<ApiResponse<UserSessionDTO[]>> {
    return this.http
      .get<ApiResponse<UserSessionDTO[]>>(`${this.baseUrl}/user/session`, {
        headers: this.getAuthHeaders(true),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Invalidate session
   * @param sessionId - string
   * @returns Observable<ApiResponse<null>>
   */
  invalidateSession(sessionId: string): Observable<ApiResponse<null>> {
    return this.http
      .delete<ApiResponse<null>>(
        `${this.baseUrl}/user/session/${sessionId}`,
        {
          headers: this.getAuthHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get user's accessible companies
   * @returns Observable<ApiResponse<UserCompaniesDTO>>
   */
  getUserCompanies(): Observable<ApiResponse<UserCompaniesDTO>> {
    return this.http
      .get<ApiResponse<UserCompaniesDTO>>(`${this.baseUrl}/user/companies`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get default headers for API requests
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  /**
   * Get headers with authorization token for protected routes
   */
  private getAuthHeaders(includeSessionId = false): HttpHeaders {
    const token = localStorage.getItem('access_token');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    });

    // Add session ID header if requested and available
    if (includeSessionId) {
      const sessionId = localStorage.getItem('session_id');
      if (sessionId) {
        headers = headers.set('X-Session-ID', sessionId);
      }
    }

    return headers;
  }

  /**
   * Handle HTTP errors
   */
  private handleError = (error: any): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage =
        'Unable to connect to server. Please check your internet connection.';
    } else if (error.status >= 400 && error.status < 500) {
      errorMessage =
        error.error?.message || 'Invalid request. Please check your input.';
    } else if (error.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    console.error('API Error:', error);
    return throwError(() => new Error(errorMessage));
  };
}
